<script setup>
import { ref, watch, onMounted } from 'vue'
import { useLocale } from "vuetify";
import { useLang } from '@/stores/lang.js'
import Item from '@/Components/Item.vue';

const { t } = useLocale();
const localeLang = useLang();
// Props
const props = defineProps({
    initialCategories: {
        type: Array,
        default: () => []
    }
})
// Reactive data
const searchQuery = ref('')
const categories = ref([])
const filteredCategories = ref([])

// Initialize categories
onMounted(() => {
    categories.value = props.initialCategories
    filteredCategories.value = categories.value
})

// Check if item matches search term in any language or description
const matchesSearchTerm = (item, searchTerm) => {
    const nameKu = (item.name?.ku || '').toLowerCase()
    const nameAr = (item.name?.ar || '').toLowerCase()
    const nameEn = (item.name?.en || '').toLowerCase()

    const descKu = (item.description?.ku || (typeof item.description === 'string' ? item.description : '') || '').toLowerCase()
    const descAr = (item.description?.ar || '').toLowerCase()
    const descEn = (item.description?.en || '').toLowerCase()

    return nameKu.includes(searchTerm) ||
        nameAr.includes(searchTerm) ||
        nameEn.includes(searchTerm) ||
        descKu.includes(searchTerm) ||
        descAr.includes(searchTerm) ||
        descEn.includes(searchTerm)
}

// Filter items by multilingual names
const filterItems = (items, searchTerm) => {
    return items.filter(item => {
        return matchesSearchTerm(item, searchTerm)
    })
}

// Main filtering function
const filterCategories = (query) => {
    if (!query || query.trim() === '') {
        filteredCategories.value = categories.value
        return
    }

    const searchTerm = query.toLowerCase().trim()

    filteredCategories.value = categories.value.map(category => {
        // Clone the category to avoid mutating original data
        const filteredCategory = { ...category }

        // Filter items in main category
        if (filteredCategory.items) {
            filteredCategory.items = filterItems(filteredCategory.items, searchTerm)
        }

        // Filter items in child categories
        if (filteredCategory.chaild && filteredCategory.chaild.length > 0) {
            filteredCategory.chaild = filteredCategory.chaild.map(child => {
                const filteredChild = { ...child }
                if (filteredChild.items) {
                    filteredChild.items = filterItems(filteredChild.items, searchTerm)
                }
                return filteredChild
            }).filter(child => child.items && child.items.length > 0) // Remove child categories with no matching items
        }

        return filteredCategory
    }).filter(category => {
        // Keep category if it has matching items or child categories with matching items
        const hasMatchingItems = category.items && category.items.length > 0
        const hasMatchingChildren = category.chaild && category.chaild.length > 0
        return hasMatchingItems || hasMatchingChildren
    })
}

// Watch for search query changes
watch(searchQuery, (newQuery) => {
    filterCategories(newQuery)
}, { immediate: true })



// Computed property for search statistics

// Expose functions to template and parent components
defineExpose({
    filterCategories,
    searchQuery,
    categories,
    filteredCategories
})
</script>
<template>
    <v-alert>{{ t("$vuetify.searchItem") }}</v-alert>
    <v-text-field clearable dir="auto" autofocus autocomplete="off" variant="outlined" density="comfortable"
        class="mx-3 mt-4" v-model="searchQuery" :label="t('$vuetify.searchItem')"
        prepend-inner-icon="mdi-magnify"></v-text-field>
    <div style="height: 250px;" class="overflow-y-auto px-3 h-100">
        <div v-if="searchQuery" v-for="category in filteredCategories" :key="category.id">
            <div v-if="category.items && category.items.length > 0" class="items">
                <v-row v-if="category.items.length > 0" class="justify-center px-1">
                    <v-col cols="10" class="pt-3 pb-0">
                        <v-divider :thickness="3" color="appBarColor" class="border-opacity-75">
                            <h6 class="text-h6">
                                {{ category["name_" + localeLang.myPrefix] }}
                            </h6>
                        </v-divider>
                    </v-col>
                    <v-col v-for="item in category.items" :key="item.id" class="px-2 pb-2" cols="11" sm="6" md="6"
                        lg="6">
                        <Item :item="item" />
                    </v-col>
                </v-row>
            </div>
        </div>
        <v-alert class="mb-3" v-if="filteredCategories.length === 0">
            {{ t("$vuetify.noResults") }}
        </v-alert>
    </div>
</template>
